import InitColorSchemeScript from '@mui/material/InitColorSchemeScript';
import { type ReactNode } from 'react';
import {
  isRouteErrorResponse,
  Links,
  Meta,
  Outlet,
  Scripts,
  ScrollRestoration,
} from 'react-router';

import { AppProviders } from '@/app/AppProviders';
import { Seo, StatusState } from '@/components/molecules';
import { preconnectOrigins, preloadFonts } from '@/config/performance';

import type { Route } from './+types/root';

export function Layout({ children }: { children: ReactNode }) {
  return (
    // InitColorSchemeScript adds the colour-scheme attribute before React
    // hydrates, so the server's <html> never matches; that is expected.
    <html lang="en" suppressHydrationWarning>
      <head>
        <meta charSet="utf-8" />
        <meta name="viewport" content="width=device-width, initial-scale=1" />
        <meta name="color-scheme" content="light dark" />
        <meta name="theme-color" content="#C53D49" />
        <link rel="icon" href="/favicon.ico" sizes="any" />
        <link rel="icon" type="image/png" sizes="32x32" href="/favicon-32x32.png" />
        <link rel="icon" type="image/png" sizes="16x16" href="/favicon-16x16.png" />
        <link rel="apple-touch-icon" sizes="180x180" href="/apple-touch-icon.png" />
        <link rel="manifest" href="/site.webmanifest" />
        {/* Open the connection to Supabase while the HTML is still parsing:
            the first data request and every vehicle photo come from there. */}
        {preconnectOrigins.map((origin) => (
          <link key={origin} rel="preconnect" href={origin} crossOrigin="anonymous" />
        ))}
        {/* The two Latin subsets carry every word on the page. Without this
            the browser only discovers them after the stylesheet parses. */}
        {preloadFonts.map((href) => (
          <link
            key={href}
            rel="preload"
            as="font"
            type="font/woff2"
            href={href}
            crossOrigin="anonymous"
          />
        ))}
        <Meta />
        <Links />
      </head>
      <body>
        {/* Sets the colour-scheme attribute before first paint, so a visitor
            never sees a flash of the wrong scheme. Defaults to light
            regardless of OS preference; ThemeModeToggle lets people opt into
            dark or system. */}
        <InitColorSchemeScript attribute="data" defaultMode="light" />
        {/* The twin of the script above, for the import promise ribbon: a
            visitor who closed it this session has the attribute set before
            the first frame, so the red bar never renders and the 40px it
            occupies never has to collapse. Keys and attribute name are
            repeated from `hooks/useImportPromiseBanner.ts` because this runs
            as plain text long before any module is evaluated. */}
        <script
          suppressHydrationWarning
          dangerouslySetInnerHTML={{
            __html:
              "try{if(sessionStorage.getItem('kbx-promise-banner')==='dismissed')" +
              "document.documentElement.setAttribute('data-kbx-promise','dismissed')}catch(e){}",
          }}
        />
        {children}
        <ScrollRestoration />
        <Scripts />
      </body>
    </html>
  );
}

export default function Root() {
  return (
    <AppProviders>
      <Outlet />
    </AppProviders>
  );
}

/** Last-resort boundary; routes under SiteLayout get the site chrome instead. */
export function ErrorBoundary({ error }: Route.ErrorBoundaryProps) {
  const notFound = isRouteErrorResponse(error) && error.status === 404;
  return (
    <AppProviders>
      <StatusState
        tone={notFound ? 'empty' : 'error'}
        title={notFound ? 'Page not found' : 'Something went wrong'}
        description={
          notFound
            ? 'The page you were looking for does not exist.'
            : 'Please refresh the page. If it keeps happening, contact us.'
        }
        action={{ label: 'Go to the home page', to: '/' }}
      />
    </AppProviders>
  );
}

export function HydrateFallback() {
  // Unknown URLs and inventory added after a deploy reach the static SPA
  // fallback. Supply a crawlable Kabex card there too; route-level Seo replaces
  // it after the application loads.
  return (
    <Seo
      title="Cars sourced and imported to Uganda"
      description="Kabex Motors helps you source a car from Japan, the UK and worldwide, with a clear quotation for importing it to Uganda."
    />
  );
}
